import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

pdfMake.vfs = pdfFonts.vfs;

const AMOUNT_KEY_PATTERN =
  /(amount|revenue|cost|profit|expense|pay|charge|price|total|balance|income|billing|payment)/i;
const COUNT_KEY_PATTERN =
  /(count|clients?|linguists?|services?|items?|records?|rows?|retained|churned|lost|totalclients|lastyearclients)/i;
const PERCENT_KEY_PATTERN =
  /(rate|percent|margin|growth|share|retention|ratio)/i;

const TABLE_COLUMN_PRIORITY = [
  "category",
  "subCategory",
  "clientName",
  "serviceType",
  "paymentMethod",
  "lastYearAmount",
  "currentAmount",
  "lastYearRevenue",
  "currentRevenue",
  "amount",
  "delta",
  "deltaPercent",
  "sharePercent",
  "count",
];

const stringifyPretty = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return "";
  }
};

const parseJsonSafe = (text) => {
  try {
    return { parsed: JSON.parse(text), isValid: true, error: "" };
  } catch (error) {
    return { parsed: null, isValid: false, error: error.message };
  }
};

const stripCodeFences = (text = "") => {
  const trimmed = String(text || "").trim();
  if (!trimmed.startsWith("```") || !trimmed.endsWith("```")) {
    return trimmed;
  }

  return trimmed
    .replace(/^```[a-zA-Z0-9_-]*\s*/, "")
    .replace(/\s*```$/, "")
    .trim();
};

const normalizeDecodedInsights = (input) => {
  if (input && typeof input === "object") {
    const findings = Array.isArray(input.findings) ? input.findings : [];
    const questions = Array.isArray(input.questions) ? input.questions : [];

    return {
      isValid: true,
      findings,
      questions,
      raw: stringifyPretty(input),
      error: "",
    };
  }

  const cleaned = stripCodeFences(input || "");
  const parsed = parseJsonSafe(cleaned);
  if (!parsed.isValid || !parsed.parsed || typeof parsed.parsed !== "object") {
    return {
      isValid: false,
      findings: [],
      questions: [],
      raw: cleaned,
      error: parsed.error || "Insights response is not valid JSON",
    };
  }

  const findings = Array.isArray(parsed.parsed.findings)
    ? parsed.parsed.findings
    : [];
  const questions = Array.isArray(parsed.parsed.questions)
    ? parsed.parsed.questions
    : [];

  return {
    isValid: true,
    findings,
    questions,
    raw: cleaned,
    error: "",
  };
};

const getSeverityColor = (severity) => {
  const normalized = String(severity || "").toLowerCase();
  if (normalized === "high") return "error";
  if (normalized === "medium") return "warning";
  if (normalized === "low") return "success";
  return "default";
};

const isAmountKey = (key = "") => AMOUNT_KEY_PATTERN.test(String(key));
const isCountKey = (key = "") => COUNT_KEY_PATTERN.test(String(key));
const isPercentKey = (key = "") => PERCENT_KEY_PATTERN.test(String(key));

const hasAmountContext = (text = "") =>
  /(revenue|cost|profit|expense|pay|charge|price|amount|balance|income|billing|payment|last year|this year)/i.test(
    String(text),
  );

const isIsoDateLike = (value = "") =>
  /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(String(value).trim());

const isYearMonthLike = (value = "") =>
  /^\d{4}-\d{2}$/.test(String(value).trim());

const formatNumeric = (value, asAmount = false) => {
  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const formatted = formatter.format(value);
  return asAmount ? `$${formatted}` : formatted;
};

const formatCount = (value) => {
  const formatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  });
  return formatter.format(value);
};

const coerceNumber = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = value.replace(/,/g, "").trim();
    if (!normalized) return null;
    if (!/^[-+]?\d*(\.\d+)?$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const formatConfidencePercent = (value) => {
  const parsed = coerceNumber(value);
  if (parsed === null) return "n/a";

  const normalized = parsed <= 1 ? parsed * 100 : parsed;
  const safe = Math.min(Math.max(normalized, 0), 100);

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: Number.isInteger(safe) ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return `${formatter.format(safe)}%`;
};

const formatScalar = (value, key = "") => {
  const keyLower = String(key || "").toLowerCase();
  if (keyLower.includes("confidence")) {
    return formatConfidencePercent(value);
  }

  if (typeof value === "string") {
    const raw = value;
    if (isIsoDateLike(raw) || isYearMonthLike(raw)) {
      return raw;
    }

    const shouldTreatAsAmount = isAmountKey(key) || hasAmountContext(raw);

    const formattedText = raw.replace(
      /(^|[^\w$-])(-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)(%?)/g,
      (match, prefix, numericPart, percentPart, offset) => {
        const trailingText = raw.slice(offset + match.length);
        const isCountLike = /^\s*(clients?|linguists?|services?|items?|records?|rows?)/i.test(
          trailingText,
        );

        const numericValue = Number(String(numericPart).replace(/,/g, ""));
        if (!Number.isFinite(numericValue)) return match;

        const formattedNumber = formatNumeric(numericValue, false);
        if (percentPart === "%") {
          return `${prefix}${formattedNumber}%`;
        }

        if (isCountLike) {
          return `${prefix}${formatCount(numericValue)}`;
        }

        if (shouldTreatAsAmount && !isCountLike) {
          return `${prefix}$${formattedNumber}`;
        }

        return Number.isInteger(numericValue)
          ? `${prefix}${formatCount(numericValue)}`
          : `${prefix}${formattedNumber}`;
      },
    );

    return formattedText;
  }

  const maybeNumber = coerceNumber(value);
  if (maybeNumber !== null) {
    if (isPercentKey(keyLower)) {
      return `${formatNumeric(maybeNumber, false)}%`;
    }
    if (isCountKey(keyLower)) {
      return formatCount(maybeNumber);
    }
    return formatNumeric(maybeNumber, isAmountKey(key));
  }
  return String(value ?? "");
};

const renderDetailValue = (value, key = "") => {
  if (Array.isArray(value)) {
    if (!value.length) return "[]";
    return value
      .map((item) => {
        if (item && typeof item === "object") return stringifyPretty(item);
        return formatScalar(item, key);
      })
      .join(" | ");
  }

  if (value && typeof value === "object") {
    return stringifyPretty(value);
  }

  return formatScalar(value, key);
};

const fieldLabel = (field = "") => {
  const key = String(field || "").split(".").pop() || "evidence";
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const isObjectArray = (value) =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((entry) => entry && typeof entry === "object" && !Array.isArray(entry));

const getEvidenceColumns = (rows = []) => {
  const discovered = [];
  const seen = new Set();

  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key);
        discovered.push(key);
      }
    });
  });

  const prioritized = TABLE_COLUMN_PRIORITY.filter((key) => seen.has(key));
  const remainder = discovered.filter((key) => !prioritized.includes(key));
  return [...prioritized, ...remainder];
};

const renderEvidenceTable = (rows, rowKeyPrefix) => {
  const columns = getEvidenceColumns(rows);
  if (!columns.length) {
    return null;
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        mt: 0.5,
        borderRadius: "8px",
        backgroundColor: "transparent",
        overflowX: "auto",
      }}
    >
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={`${rowKeyPrefix}-head-${column}`} sx={{ fontWeight: 700 }}>
                {fieldLabel(column)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={`${rowKeyPrefix}-row-${rowIndex}`}>
              {columns.map((column) => (
                <TableCell
                  key={`${rowKeyPrefix}-row-${rowIndex}-${column}`}
                  sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}
                >
                  {renderDetailValue(row?.[column], column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const getInsightsMetrics = (findings = [], questions = []) => {
  const severityCounts = findings.reduce(
    (acc, finding) => {
      const level = String(finding?.severity || "").toLowerCase();
      if (level === "high") acc.high += 1;
      else if (level === "medium") acc.medium += 1;
      else if (level === "low") acc.low += 1;
      return acc;
    },
    { high: 0, medium: 0, low: 0 },
  );

  const confidenceValues = findings
    .map((finding) => coerceNumber(finding?.confidence))
    .filter((value) => value !== null);

  const avgConfidence = confidenceValues.length
    ? confidenceValues.reduce((sum, value) => sum + value, 0) /
      confidenceValues.length
    : null;

  return {
    totalFindings: findings.length,
    totalQuestions: questions.length,
    highFindings: severityCounts.high,
    mediumFindings: severityCounts.medium,
    lowFindings: severityCounts.low,
    avgConfidence,
  };
};

const sanitizeFileNamePart = (value, fallback = "Insights") => {
  const cleaned = String(value || "")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || fallback;
};

const monthToNumber = (month) => {
  const value = String(month || "").trim();
  const map = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };
  if (map[value]) return map[value];
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 12) {
    return String(parsed).padStart(2, "0");
  }
  return null;
};

const getFilterDateLabel = (reportContext = {}) => {
  const filters = reportContext?.filters || {};
  const startDate = String(filters?.startDate || "").trim();
  const endDate = String(filters?.endDate || "").trim();

  const years = Array.isArray(filters?.years)
    ? filters.years.map((y) => Number(y)).filter(Number.isFinite).sort((a, b) => a - b)
    : [];
  const months = Array.isArray(filters?.months)
    ? filters.months
        .map(monthToNumber)
        .filter(Boolean)
        .sort((a, b) => Number(a) - Number(b))
    : [];

  if (years.length > 0 && months.length > 0) {
    return `${months[0]}-${years[0]} ${months[months.length - 1]}-${years[years.length - 1]}`;
  }

  if (isIsoDateLike(startDate) && isIsoDateLike(endDate)) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const inclusiveEnd = new Date(end);
      if (
        inclusiveEnd.getUTCDate() === 1 &&
        (inclusiveEnd.getUTCHours() === 0 || Number.isNaN(inclusiveEnd.getUTCHours()))
      ) {
        inclusiveEnd.setUTCDate(inclusiveEnd.getUTCDate() - 1);
      }

      const startLabel = `${String(start.getUTCMonth() + 1).padStart(2, "0")}-${start.getUTCFullYear()}`;
      const endLabel = `${String(inclusiveEnd.getUTCMonth() + 1).padStart(2, "0")}-${inclusiveEnd.getUTCFullYear()}`;
      return `${startLabel} ${endLabel}`;
    }
  }

  if (years.length > 0) {
    return `${years[0]} ${years[years.length - 1]}`;
  }

  return "All Dates";
};

const getInsightsPdfFileName = (reportContext = {}) => {
  const reportName =
    reportContext?.selectedReportLabel || reportContext?.selectedReportId || "Insights";
  const reportLabel = sanitizeFileNamePart(reportName, "Insights");
  const dateLabel = sanitizeFileNamePart(getFilterDateLabel(reportContext), "All Dates");
  return `${reportLabel} ${dateLabel}.pdf`;
};

const getSeverityPdfTheme = (severity) => {
  const level = String(severity || "").toLowerCase();
  if (level === "high") {
    return { label: "HIGH", accent: "#dc2626", chipFill: "#fee2e2", chipText: "#991b1b" };
  }
  if (level === "medium") {
    return { label: "MEDIUM", accent: "#d97706", chipFill: "#fef3c7", chipText: "#92400e" };
  }
  return { label: "LOW", accent: "#16a34a", chipFill: "#dcfce7", chipText: "#166534" };
};

const buildPdfEvidenceContent = (evidence = []) => {
  if (!Array.isArray(evidence) || evidence.length === 0) {
    return [{ text: "No evidence provided", margin: [0, 3, 0, 6], color: "#6b7280" }];
  }

  return evidence.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [{ text: formatScalar(item, "evidence"), margin: [0, 3, 0, 6] }];
    }

    const field = item.field || "evidence";
    const value = item.value;
    const heading = { text: fieldLabel(field), style: "sectionLabel", margin: [0, 6, 0, 3] };

    if (isObjectArray(value)) {
      const columns = getEvidenceColumns(value);
      const body = [
        columns.map((column) => ({ text: fieldLabel(column), style: "tableHeader" })),
        ...value.map((row) => columns.map((column) => renderDetailValue(row?.[column], column))),
      ];

      return [
        heading,
        {
          table: { headerRows: 1, body },
          layout: {
            hLineColor: () => "#e5e7eb",
            vLineColor: () => "#e5e7eb",
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            paddingTop: () => 5,
            paddingBottom: () => 5,
            paddingLeft: () => 6,
            paddingRight: () => 6,
          },
          margin: [0, 0, 0, 8],
          fontSize: 9,
        },
      ];
    }

    if (value && typeof value === "object") {
      const rows = Object.entries(value).map(([k, v]) => [fieldLabel(k), renderDetailValue(v, k)]);
      return [
        heading,
        {
          table: {
            headerRows: 1,
            body: [
              [
                { text: "Field", style: "tableHeader" },
                { text: "Value", style: "tableHeader" },
              ],
              ...rows,
            ],
          },
          layout: {
            hLineColor: () => "#e5e7eb",
            vLineColor: () => "#e5e7eb",
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            paddingTop: () => 5,
            paddingBottom: () => 5,
            paddingLeft: () => 6,
            paddingRight: () => 6,
          },
          margin: [0, 0, 0, 8],
          fontSize: 9,
        },
      ];
    }

    return [heading, { text: renderDetailValue(value, field), margin: [0, 0, 0, 6] }];
  });
};

const buildPdfDocDefinition = ({ findings, questions, metrics, reportContext }) => {
  const reportLabel = reportContext?.selectedReportLabel || "Insights";
  const dateLabel = getFilterDateLabel(reportContext);

  const findingsContent = findings.length
    ? findings.map((finding, idx) => {
        const severityTheme = getSeverityPdfTheme(finding?.severity);
        return {
          table: {
            widths: [6, "*"],
            body: [
              [
                {
                  text: "",
                  fillColor: severityTheme.accent,
                  border: [false, false, false, false],
                },
                {
                  border: [true, true, true, true],
                  borderColor: ["#d1d5db", "#d1d5db", "#d1d5db", "#d1d5db"],
                  margin: [10, 8, 10, 8],
                  stack: [
                    {
                      columns: [
                        {
                          width: "*",
                          text: `${idx + 1}. ${finding?.title || `Finding ${idx + 1}`}`,
                          style: "findingTitle",
                        },
                        {
                          width: "auto",
                          text: severityTheme.label,
                          bold: true,
                          color: severityTheme.chipText,
                          fillColor: severityTheme.chipFill,
                          margin: [6, 2, 6, 2],
                        },
                      ],
                    },
                    {
                      text: `Confidence: ${formatConfidencePercent(finding?.confidence)}  |  Certainty: ${String(
                        finding?.certainty || "confirmed",
                      )}`,
                      style: "meta",
                      margin: [0, 2, 0, 4],
                    },
                    { text: "Evidence", style: "sectionLabel" },
                    ...buildPdfEvidenceContent(finding?.evidence),
                    {
                      text: `Impact: ${
                        renderDetailValue(finding?.impact, "impact") || "No impact provided"
                      }`,
                      style: "bodyText",
                    },
                    {
                      text: `Recommended Action: ${
                        renderDetailValue(finding?.recommendedAction, "recommendedAction") ||
                        "No recommended action provided"
                      }`,
                      style: "bodyText",
                      margin: [0, 3, 0, 0],
                    },
                  ],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
          },
          margin: [0, 0, 0, 10],
        };
      })
    : [{ text: "No findings were returned.", italics: true }];

  return {
    pageSize: "A4",
    pageMargins: [24, 28, 24, 28],
    content: [
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                fillColor: "#0f172a",
                border: [false, false, false, false],
                margin: [12, 10, 12, 10],
                stack: [
                  { text: reportLabel, style: "title" },
                  { text: `Date Scope: ${dateLabel}`, style: "metaLight" },
                  { text: `Generated: ${new Date().toLocaleString("en-US")}`, style: "metaLight" },
                ],
              },
            ],
          ],
        },
        layout: { hLineWidth: () => 0, vLineWidth: () => 0 },
        margin: [0, 0, 0, 10],
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", "*", "*"],
          body: [
            [
              { text: "Total Findings", style: "tableHeader" },
              { text: "High Severity", style: "tableHeader" },
              { text: "Avg Confidence", style: "tableHeader" },
            ],
            [
              { text: String(metrics.totalFindings), style: "kpiValue" },
              { text: String(metrics.highFindings), style: "kpiValue" },
              {
                text: metrics.avgConfidence !== null ? formatConfidencePercent(metrics.avgConfidence) : "n/a",
                style: "kpiValue",
              },
            ],
          ],
        },
        layout: {
          hLineColor: () => "#d1d5db",
          vLineColor: () => "#d1d5db",
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          paddingTop: () => 7,
          paddingBottom: () => 7,
          paddingLeft: () => 8,
          paddingRight: () => 8,
        },
        margin: [0, 0, 0, 10],
      },
      { text: "Findings", style: "h2" },
      ...findingsContent,
      { text: "Follow-up Questions", style: "h2", margin: [0, 4, 0, 4] },
      ...(questions.length
        ? questions.map((question, idx) => ({ text: `${idx + 1}. ${renderDetailValue(question, "question")}` }))
        : [{ text: "No follow-up questions returned.", italics: true }]),
    ],
    styles: {
      title: { fontSize: 18, bold: true, color: "#ffffff", margin: [0, 0, 0, 3] },
      h2: { fontSize: 13, bold: true, margin: [0, 8, 0, 4] },
      meta: { fontSize: 9, color: "#4b5563" },
      metaLight: { fontSize: 9, color: "#cbd5e1" },
      findingTitle: { fontSize: 12, bold: true, color: "#111827", margin: [0, 0, 0, 1] },
      sectionLabel: { fontSize: 10, bold: true, color: "#374151" },
      tableHeader: { bold: true, fillColor: "#f3f4f6", color: "#111827" },
      kpiValue: { bold: true, fontSize: 12, color: "#111827" },
      bodyText: { fontSize: 10, color: "#1f2937", lineHeight: 1.2 },
    },
    defaultStyle: {
      fontSize: 10,
    },
  };
};

const AIInsightsViewOverlay = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();
  const [serverInsights, setServerInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const decodedResponseText =
    overlayData?.decodedResponseText || overlayData?.insightsData || "";
  const reportContext = useMemo(
    () => overlayData?.basePayload?.context || overlayData?.context || {},
    [overlayData],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchInsights = async () => {
      const queryFilters = reportContext?.filters || {};
      const selectedReportId = reportContext?.selectedReportId || null;
      const selectedCategoryId = reportContext?.categoryId || null;
      setIsLoading(true);

      try {
        const response = await api.get("/reports/insights", {
          params: {
            ...queryFilters,
            reportId: selectedReportId,
            categoryId: selectedCategoryId,
          },
        });

        if (isMounted) {
          setServerInsights(response?.data?.data || null);
        }
      } catch (_error) {
        if (isMounted) {
          setServerInsights(null);
          showSnackbar("Unable to load deterministic insights.", theme, "error");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchInsights();

    return () => {
      isMounted = false;
    };
  }, [reportContext, showSnackbar, theme]);

  const insights = useMemo(
    () => normalizeDecodedInsights(serverInsights || decodedResponseText),
    [decodedResponseText, serverInsights],
  );

  const metrics = useMemo(
    () => getInsightsMetrics(insights.findings, insights.questions),
    [insights.findings, insights.questions],
  );

  const closeOverlay = () => setOverlayName("");

  const exportPdf = () => {
    if (!insights.isValid) {
      showSnackbar("No parsed insights to export.", theme, "info");
      return;
    }

    const docDefinition = buildPdfDocDefinition({
      findings: insights.findings,
      questions: insights.questions,
      metrics,
      reportContext,
    });
    const fileName = getInsightsPdfFileName(reportContext);
    pdfMake.createPdf(docDefinition).download(fileName);
  };

  return (
    <Overlay
      id="ai-insights"
      open={true}
      onClose={closeOverlay}
      title="Insights"
      footer={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "center" }}>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfRoundedIcon />}
            onClick={exportPdf}
            size="large"
            sx={{ borderRadius: "12px" }}
          >
            Export PDF
          </Button>
        </Box>
      }
    >

        <Box
          sx={{
            p: "1rem 2.3rem 1rem 2.5rem",
            flex: "1 1 auto",
            overflowY: "auto",
            width: "100%",
            minHeight: 0,
          }}
        >
          <Stack spacing={1.5}>
            {isLoading ? (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "12px" }}>
                <Typography variant="body2" color="text.secondary">
                  Loading insights...
                </Typography>
              </Paper>
            ) : null}

            {insights.isValid ? (
              <Chip
                icon={<FactCheckRoundedIcon />}
                label={`${insights.findings.length} findings, ${insights.questions.length} question(s)`}
                color="info"
                variant="outlined"
                sx={{ alignSelf: "flex-start" }}
              />
            ) : (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "12px" }}>
                <Typography variant="body2" color="text.secondary">
                  Could not parse decoded insights JSON: {insights.error}
                </Typography>
              </Paper>
            )}

            {insights.isValid ? (
              <Grid2 container spacing={1.25}>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "12px", borderColor: theme.palette.info.main }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Total Findings
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {metrics.totalFindings}
                    </Typography>
                  </Paper>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "12px", borderColor: theme.palette.error.main }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      High Severity
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {metrics.highFindings}
                    </Typography>
                  </Paper>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "12px", borderColor: theme.palette.warning.main }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Medium Severity
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {metrics.mediumFindings}
                    </Typography>
                  </Paper>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: "12px", borderColor: theme.palette.success.main }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Avg Confidence
                    </Typography>
                    <Typography variant="h4" fontWeight={800}>
                      {metrics.avgConfidence !== null
                        ? formatConfidencePercent(metrics.avgConfidence)
                        : "n/a"}
                    </Typography>
                  </Paper>
                </Grid2>
              </Grid2>
            ) : null}

            {insights.isValid && insights.findings.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "12px" }}>
                <Typography variant="body2" color="text.secondary">
                  No findings were returned.
                </Typography>
              </Paper>
            ) : null}

            {insights.isValid
              ? insights.findings.map((finding, idx) => {
                  const evidence = Array.isArray(finding?.evidence)
                    ? finding.evidence
                    : [];

                  const extraEntries = Object.entries(finding || {}).filter(
                    ([key]) =>
                      ![
                        "title",
                        "severity",
                        "confidence",
                        "evidence",
                        "impact",
                        "recommendedAction",
                        "details",
                        "certainty",
                        "missingData",
                      ].includes(key),
                  );

                  return (
                    <Paper
                      key={`finding-${idx}`}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.background.default,
                        borderLeftWidth: "6px",
                        borderLeftStyle: "solid",
                        borderLeftColor:
                          finding?.severity === "high"
                            ? theme.palette.error.main
                            : finding?.severity === "medium"
                              ? theme.palette.warning.main
                              : theme.palette.success.main,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" fontWeight={700}>
                          {finding?.title || `Finding ${idx + 1}`}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={`Severity: ${finding?.severity || "n/a"}`}
                            color={getSeverityColor(finding?.severity)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Confidence: ${formatConfidencePercent(finding?.confidence)}`}
                            size="small"
                            variant="outlined"
                          />
                          {finding?.certainty ? (
                            <Chip
                              label={`Certainty: ${String(finding.certainty)}`}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                      </Box>

                      <Stack spacing={1}>
                        {evidence.length > 0 ? (
                          <Paper
                            variant="outlined"
                            sx={{ p: 1, borderRadius: "10px", backgroundColor: theme.palette.background.alt }}
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              Evidence
                            </Typography>
                            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                              {evidence.map((item, itemIdx) => {
                                if (!item || typeof item !== "object") {
                                  return (
                                    <Typography
                                      key={`e-${idx}-${itemIdx}`}
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {formatScalar(item, "evidence")}
                                    </Typography>
                                  );
                                }

                                const field = item.field || "evidence";
                                const value = item.value;
                                const valueIsObjectArray = isObjectArray(value);

                                return (
                                  <Box key={`e-${idx}-${itemIdx}`}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                      {fieldLabel(field)}
                                    </Typography>
                                    {valueIsObjectArray ? (
                                      renderEvidenceTable(value, `e-${idx}-${itemIdx}`)
                                    ) : value && typeof value === "object" ? (
                                      renderEvidenceTable([value], `e-${idx}-${itemIdx}`)
                                    ) : (
                                      <Typography variant="body2" color="text.secondary">
                                        {renderDetailValue(value, field)}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Paper>
                        ) : null}

                        <Paper
                          variant="outlined"
                          sx={{ p: 1, borderRadius: "10px", backgroundColor: theme.palette.background.alt }}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            Impact
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {renderDetailValue(finding?.impact, "impact") ||
                              "No impact provided"}
                          </Typography>
                        </Paper>

                        <Paper
                          variant="outlined"
                          sx={{ p: 1, borderRadius: "10px", backgroundColor: theme.palette.background.alt }}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            Recommended Action
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {renderDetailValue(
                              finding?.recommendedAction,
                              "recommendedAction",
                            ) || "No recommended action provided"}
                          </Typography>
                        </Paper>
                      </Stack>

                      {extraEntries.length > 0 ? (
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>
                            Additional Details
                          </Typography>
                          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                            {extraEntries.map(([key, value]) => (
                              <Box key={`${idx}-${key}`}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                  {fieldLabel(key)}
                                </Typography>
                                {isObjectArray(value) ? (
                                  renderEvidenceTable(value, `d-${idx}-${key}`)
                                ) : value && typeof value === "object" ? (
                                  renderEvidenceTable([value], `d-${idx}-${key}`)
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    {renderDetailValue(value, key)}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ) : null}
                    </Paper>
                  );
                })
              : null}

            {insights.isValid ? (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "12px" }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                  Follow-up Questions
                </Typography>
                {insights.questions.length > 0 ? (
                  <Stack spacing={0.5}>
                    {insights.questions.map((question, idx) => (
                      <Typography
                        key={`question-${idx}`}
                        variant="body2"
                        color="text.secondary"
                      >
                        {idx + 1}. {renderDetailValue(question, "question")}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No follow-up questions returned.
                  </Typography>
                )}
              </Paper>
            ) : null}
          </Stack>
        </Box>
    </Overlay>
  );
};

export default AIInsightsViewOverlay;
